import {
  Component,
  ElementRef,
  Inject,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { formatDate } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Chart, registerables } from 'chart.js';
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  startWith,
} from 'rxjs';
import { parseDayString } from '../../../../../shared/helper/datehelper';
import {
  AttendanceRegularityModel,
  AttendanceUsersQueryModel,
} from '../../../../../shared/store/attendance/user-work-attendance/types';
import { UserWorkAttendanceService } from '../../../../../shared/store/attendance/user-work-attendance/user-work-attendance.service';
import { ListItem } from '../../../../../shared/store/typeahead/types';
import {
  AttendanceChart,
  toDurationLabel,
  toTimeLabel,
} from '../shared/attendance-chart';

/**
 * "Présence par commercial": one salesperson over a period, one working day per row, most recent on
 * top. Same endpoint as the team dashboard, with a single userIds. The salesperson lives in the URL,
 * the period in the subheader.
 */
@UntilDestroy()
@Component({
  selector: 'app-sales-person-attendance-dashboard',
  standalone: false,
  templateUrl: './sales-person-attendance-dashboard.component.html',
  styleUrl: './sales-person-attendance-dashboard.component.scss',
})
export class SalesPersonAttendanceDashboardComponent
  implements OnInit, OnDestroy
{
  @ViewChild('chartCanvas', { static: true })
  chartCanvas: ElementRef<HTMLCanvasElement>;

  userId = signal<number | null>(null);

  presenceCount = signal('—');
  medianStart = signal('—');
  medianEnd = signal('—');
  medianAmplitude = signal('—');

  hasNoPresence = signal(false);
  /** The groups picked offer no salesperson. */
  hasNoUsers = signal(false);
  /** Salesperson drawn, for the picker to offer them even when its list would not. */
  displayedUser = signal<ListItem | null>(null);
  chartHeightPx = signal(0);

  /** Id of the URL; its child route is rebuilt on every navigation, so it is read once each ends. */
  userId$: Observable<number | null>;
  salesPersonUsers$: Observable<ListItem[]>;
  salesPersonRegularity$: Observable<AttendanceRegularityModel>;
  salesPersonRegularityError$: Observable<HttpErrorResponse | null>;

  private chart: AttendanceChart;
  private query: AttendanceUsersQueryModel | null = null;

  constructor(
    private readonly userWorkAttendanceService: UserWorkAttendanceService,
    private readonly translateService: TranslateService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    @Inject(LOCALE_ID) private readonly locale: string,
  ) {
    this.userId$ = this.router.events.pipe(
      filter((x) => x instanceof NavigationEnd),
      startWith(null),
      map(
        () =>
          Number(this.route.firstChild?.snapshot.paramMap.get('userId')) ||
          null,
      ),
      distinctUntilChanged(),
    );
    this.salesPersonUsers$ =
      this.userWorkAttendanceService.getSalesPersonUsers.value$.pipe(
        filter((x) => !!x),
      );
    this.salesPersonRegularity$ =
      this.userWorkAttendanceService.getSalesPersonRegularity.value$;
    this.salesPersonRegularityError$ =
      this.userWorkAttendanceService.getSalesPersonRegularity.error$;
  }

  ngOnInit(): void {
    Chart.register(...registerables);

    this.chart = new AttendanceChart(() =>
      this.translateService.instant('ATTENDANCE_LBL_WORK_SCHEDULE'),
    );

    this.salesPersonRegularity$
      .pipe(
        filter((x) => !!x),
        untilDestroyed(this),
      )
      .subscribe((payload) => this.render(payload));

    // A failed call keeps the previous value in the slice: never leave the previous period drawn.
    this.salesPersonRegularityError$
      .pipe(
        filter((x) => !!x),
        untilDestroyed(this),
      )
      .subscribe(() => this.clear());

    // Same page, other id: the previous salesperson is cleared first.
    this.userId$.pipe(untilDestroyed(this)).subscribe((userId) => {
      this.userId.set(userId);
      this.userWorkAttendanceService.getSalesPersonRegularity.reset();
      this.clear();
      this.load();
    });

    this.salesPersonUsers$
      .pipe(untilDestroyed(this))
      .subscribe((users) => this.hasNoUsers.set(users.length === 0));

    // Arriving without an id (menu, first load, group back from empty): the first salesperson offered.
    combineLatest([this.userId$, this.salesPersonUsers$])
      .pipe(
        filter(([userId, users]) => !userId && users.length > 0),
        untilDestroyed(this),
      )
      .subscribe(([, users]) =>
        this.router.navigate(
          ['/Crm/SalesPersonAttendanceDashboard', users[0].id],
          { replaceUrl: true },
        ),
      );
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
    this.userWorkAttendanceService.getSalesPersonRegularity.reset();
  }

  onQueryChange(query: AttendanceUsersQueryModel): void {
    this.query = query;
    this.load();
  }

  /** Switching salesperson goes through the URL, the screen's single way to load one; null empties it. */
  onSalesPersonChange(userId: number | null): void {
    if (userId === this.userId()) return;

    this.router.navigate(
      userId
        ? ['/Crm/SalesPersonAttendanceDashboard', userId]
        : ['/Crm/SalesPersonAttendanceDashboard'],
    );
  }

  private load(): void {
    const userId = this.userId();

    if (!userId || !this.query) return;

    this.userWorkAttendanceService.getSalesPersonRegularity.call({
      ...this.query,
      userIds: [userId],
    });
  }

  private render(payload: AttendanceRegularityModel): void {
    // No row: a former employee without presence over the period, or a period without a working day.
    const user = payload.users[0];

    this.presenceCount.set(`${payload.presenceCount}`);
    this.medianStart.set(toTimeLabel(payload.medianStartMinutes));
    this.medianEnd.set(toTimeLabel(payload.medianEndMinutes));
    this.medianAmplitude.set(toDurationLabel(payload.medianAmplitudeMinutes));

    const rows = (user?.days ?? [])
      .slice()
      .reverse()
      .map((day) => ({
        label: formatDate(parseDayString(day.date), 'EEE dd/MM', this.locale),
        day,
        muted: false,
      }));

    this.chart.render(this.chartCanvas.nativeElement, rows);
    this.chartHeightPx.set(this.chart.heightPx);
    this.hasNoPresence.set(rows.length === 0);
    this.displayedUser.set(
      user ? { id: user.userId, name: user.userContactName, value: '' } : null,
    );
  }

  private clear(): void {
    this.presenceCount.set('—');
    this.medianStart.set('—');
    this.medianEnd.set('—');
    this.medianAmplitude.set('—');

    this.chart.render(this.chartCanvas.nativeElement, []);
    this.chartHeightPx.set(0);
    this.hasNoPresence.set(false);
    this.displayedUser.set(null);
  }
}
